import { apiGet, apiPost, apiDelete } from './client';
import type { OperationResult } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Test prompts — DB-first manual QA library.
// Backed by ai.AiAgentTestPrompt + /api/AiAgentTestPrompt/*.
// Surfaces on the Agent detail → Test prompts tab.
// ─────────────────────────────────────────────────────────────────────────────

export interface AgentTestPrompt {
  id: number;
  aiAgentId: number;
  category?: string | null;
  subcategory?: string | null;
  role?: string | null;
  prompt: string;
  whatThisTests?: string | null;
  whatToLookFor?: string | null;
  title?: string | null;
  /** Short stable handle (e.g., "T1", "GLU-03"). */
  code?: string | null;
  /** Comma-separated tag string. */
  tags?: string | null;
  safetyCritical: boolean;
  peerConsultationExpected: boolean;
  lastTestedAtUtc?: string | null;
  lastTestedBy?: number | null;
  /** "pass" | "fail" | "skip" | null. */
  lastResultStatus?: 'pass' | 'fail' | 'skip' | null;
  lastResultNotes?: string | null;
  sourceFilePath?: string | null;
  isActive: boolean;
  displayOrder: number;
  createdDate?: string | null;
  lastUpdatedDate?: string | null;
}

/** All active test prompts for one agent, ordered for stable rendering. */
export async function getTestPromptsForAgent(agentId: number): Promise<AgentTestPrompt[]> {
  const wrapped = await apiGet<OperationResult<AgentTestPrompt[]>>(
    `/api/AiAgentTestPrompt/ForAgent/${agentId}`,
  );
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load test prompts');
  return wrapped.data ?? [];
}

export async function getTestPromptById(id: number): Promise<AgentTestPrompt> {
  const wrapped = await apiGet<OperationResult<AgentTestPrompt>>(`/api/AiAgentTestPrompt/GetById/${id}`);
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Failed to load prompt');
  return wrapped.data;
}

export async function addTestPrompt(prompt: Partial<AgentTestPrompt>): Promise<AgentTestPrompt> {
  const wrapped = await apiPost<OperationResult<AgentTestPrompt>>('/api/AiAgentTestPrompt/Add', prompt);
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Failed to create prompt');
  return wrapped.data;
}

export async function updateTestPrompt(prompt: AgentTestPrompt): Promise<AgentTestPrompt> {
  const wrapped = await apiPost<OperationResult<AgentTestPrompt>>('/api/AiAgentTestPrompt/Update', prompt);
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Failed to save prompt');
  return wrapped.data;
}

export async function removeTestPrompt(id: number): Promise<void> {
  const wrapped = await apiDelete<OperationResult<unknown>>(`/api/AiAgentTestPrompt/Remove/${id}`);
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to delete prompt');
}

/**
 * Stamp the outcome of a manual run for one prompt. The tester id is set
 * server-side from the JWT — clients only send {promptId, status, notes}.
 */
export async function markTestPromptResult(
  promptId: number,
  status: 'pass' | 'fail' | 'skip',
  notes?: string,
): Promise<AgentTestPrompt> {
  const wrapped = await apiPost<OperationResult<AgentTestPrompt>>(
    '/api/AiAgentTestPrompt/MarkResult',
    { promptId, status, notes },
  );
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Failed to mark result');
  return wrapped.data;
}
